/*
 * Magazyn treści CMS.
 *
 * Sterownik "api" (domyślny) czyta treść z GET /api/content i zapisuje ją
 * przez endpointy panelu. Żeby strona nie migała pustką przy każdym wejściu,
 * ostatnia odpowiedź serwera trafia do localStorage i służy za pierwszy render,
 * a w tle leci odświeżenie (stale-while-revalidate).
 *
 * Sterownik "local" trzyma wszystko wyłącznie w localStorage — przydaje się
 * do pracy nad frontem bez uruchomionego backendu:
 *   REACT_APP_CMS_DRIVER=local npm start
 */

import { useSyncExternalStore, useCallback } from "react";
import { SEED } from "./seed.js";
import { api } from "../api.js";

export const DRIVER = process.env.REACT_APP_CMS_DRIVER || "api";

const CACHE_KEY = "prawly_cms_cache_v1";
const LOCAL_KEY = "prawly_cms_v1";
const STORE_KEY = DRIVER === "api" ? CACHE_KEY : LOCAL_KEY;

/* Gałęzie treści zapisywane jako całość (bez wpisów, te mają własne endpointy). */
const BRANCHES = [
  "settings",
  "home",
  "about",
  "team",
  "howItWorks",
  "pricing",
  "contact",
  "faq",
  "legal",
  "caseFlags",
  "emailTemplates",
  "landings",
  "landingShared",
  "googleReviews",
];

/* ---------------------------------------------------------------- stan */

const clone = (v) =>
  typeof structuredClone === "function"
    ? structuredClone(v)
    : JSON.parse(JSON.stringify(v));

const isPlainObject = (v) =>
  v !== null && typeof v === "object" && !Array.isArray(v);

/**
 * Scala treść z serwera na treści fabrycznej — rekurencyjnie.
 *
 * Scalanie płytkie nie wystarcza: gdy w bazie leży `about` zapisane przed
 * dodaniem w kodzie sekcji `about.firm`, płytkie nadpisanie usuwa tę sekcję
 * i strona wywala się na odwołaniu do brakującego pola. Seed pełni więc rolę
 * wartości domyślnych na każdym poziomie zagnieżdżenia.
 *
 * Tablice podmieniamy w całości — lista pytań FAQ czy osób w zespole ma
 * wyglądać dokładnie tak, jak zapisał ją administrator, bez doklejania
 * pozycji fabrycznych.
 */
function deepMerge(base, override) {
  if (!isPlainObject(base) || !isPlainObject(override)) {
    return override === undefined ? base : override;
  }
  const out = { ...base };
  for (const [k, v] of Object.entries(override)) {
    out[k] = isPlainObject(v) && isPlainObject(base[k]) ? deepMerge(base[k], v) : v;
  }
  return out;
}

function read() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return clone(SEED);
    // Brakujące gałęzie uzupełniamy seedem — dzięki temu dołożenie nowej
    // sekcji w kodzie nie wywraca strony przed jej zapisaniem w bazie.
    return deepMerge(clone(SEED), JSON.parse(raw));
  } catch {
    return clone(SEED);
  }
}

let state = read();
let snapshot = state;
let status = DRIVER === "api" ? "loading" : "ready"; // loading | ready | error
let statusSnapshot = status;

const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

function persist(next) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(next));
  } catch {
    /* przekroczony limit storage — treść zostaje w pamięci sesji */
  }
}

function commit(next, { save = true } = {}) {
  state = next;
  snapshot = next;
  if (save) persist(next);
  notify();
}

function setStatus(s) {
  status = s;
  statusSnapshot = s;
  notify();
}

const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

/* -------------------------------------------------------------- odczyt */

export function useCms() {
  return useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
}

/** Wycinek treści, np. useContent("home") albo useContent("posts"). */
export function useContent(key) {
  return useSyncExternalStore(
    subscribe,
    useCallback(() => snapshot[key], [key]),
    useCallback(() => snapshot[key], [key])
  );
}

/** Stan wczytywania treści z serwera: "loading" | "ready" | "error". */
export function useCmsStatus() {
  return useSyncExternalStore(
    subscribe,
    () => statusSnapshot,
    () => statusSnapshot
  );
}

export const getContent = () => state;

/* ------------------------------------------------------- pobranie treści */

let inFlight = null;

/**
 * Wczytuje treść z serwera. Wywoływane raz przy starcie aplikacji oraz po
 * zalogowaniu — administrator dostaje w odpowiedzi także wpisy nieopublikowane.
 */
export function loadContent({ quiet = false } = {}) {
  if (DRIVER !== "api") return Promise.resolve(state);
  if (inFlight) return inFlight;

  if (!quiet) setStatus("loading");

  inFlight = api("/content")
    .then((data) => {
      if (!data || typeof data !== "object") throw new Error("Pusta odpowiedź serwera");
      const next = deepMerge(clone(SEED), data);
      commit(next);
      setStatus("ready");
      return next;
    })
    .catch((e) => {
      // Zostawiamy to, co już mamy (cache albo treść fabryczna) — strona
      // ma się wyświetlić także wtedy, gdy API jest chwilowo niedostępne.
      setStatus("error");
      console.warn("[cms] nie udało się wczytać treści:", e.message);
      return state;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

/* ------------------------------------------------------------- zapisy */

const putBranch = (key, value) =>
  api(`/content/${key}`, { method: "PUT", body: { data: value } });

/*
 * Zapisy są pesymistyczne: najpierw serwer, dopiero potem stan lokalny.
 * Dzięki temu nieudany zapis nie rusza tego, co widzi użytkownik — wersja
 * robocza w edytorze zostaje nietknięta i można spróbować ponownie.
 */

export const cms = {
  /** Podmienia całą gałąź, np. cms.replace("faq", [...]). */
  async replace(key, value) {
    if (DRIVER === "api") await putBranch(key, value);
    commit({ ...state, [key]: value });
  },

  /** Dodaje lub aktualizuje element kolekcji po `id`. */
  async upsert(key, item) {
    let saved = item;

    if (DRIVER === "api") {
      if (key === "posts") {
        saved = await api(`/content/posts/${item.id}`, { method: "PUT", body: item });
      } else {
        const list = state[key] || [];
        const idx = list.findIndex((x) => x.id === item.id);
        await putBranch(
          key,
          idx === -1 ? [item, ...list] : list.map((x, i) => (i === idx ? item : x))
        );
      }
    }

    const list = state[key] || [];
    const idx = list.findIndex((x) => x.id === saved.id);
    commit({
      ...state,
      [key]: idx === -1 ? [saved, ...list] : list.map((x, i) => (i === idx ? saved : x)),
    });
    return saved;
  },

  /** Usuwa element kolekcji po `id`. */
  async remove(key, id) {
    const rest = (state[key] || []).filter((x) => x.id !== id);

    if (DRIVER === "api") {
      if (key === "posts") await api(`/content/posts/${id}`, { method: "DELETE" });
      else await putBranch(key, rest);
    }

    commit({ ...state, [key]: rest });
  },

  /** Przesuwa element kolekcji o `delta` pozycji. */
  async move(key, id, delta) {
    const list = [...(state[key] || [])];
    const i = list.findIndex((x) => x.id === id);
    const j = i + delta;
    if (i === -1 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    await cms.replace(key, list);
  },

  /**
   * Przywraca treść fabryczną. Bez argumentu resetuje wszystkie sekcje stron;
   * baza wiedzy pozostaje nietknięta — wpisy to dane redakcyjne i kasujemy je
   * wyłącznie pojedynczo, świadomą decyzją.
   */
  async reset(key) {
    if (key) {
      if (key === "posts")
        throw new Error("Wpisy usuwa się pojedynczo w zakładce Baza wiedzy.");
      return cms.replace(key, clone(SEED[key]));
    }

    if (DRIVER !== "api") {
      localStorage.removeItem(STORE_KEY);
      return commit(clone(SEED), { save: false });
    }

    for (const k of BRANCHES) {
      // sekwencyjnie — przy błędzie zatrzymujemy się na pierwszej sekcji
      await cms.replace(k, clone(SEED[k]));
    }
  },

  /** Kopia zapasowa treści jako JSON. */
  export() {
    return JSON.stringify(state, null, 2);
  },

  /** Wgranie kopii zapasowej. Rzuca wyjątkiem przy niepoprawnym JSON-ie. */
  async import(json) {
    const parsed = JSON.parse(json);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      throw new Error("Plik nie zawiera obiektu z treścią.");

    if (DRIVER !== "api") return commit(deepMerge(clone(SEED), parsed));

    for (const k of BRANCHES) {
      if (parsed[k] !== undefined) await cms.replace(k, parsed[k]);
    }
    for (const p of Array.isArray(parsed.posts) ? parsed.posts : []) {
      await cms.upsert("posts", p);
    }
  },

  /** Ponowne pobranie treści z serwera. */
  refresh: () => loadContent({ quiet: true }),
};

/* ------------------------------------------------------------ narzędzia */

export const uid = (prefix = "x") =>
  `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[ąćęłńóśźż]/g, (c) => "acelnoszz"["ąćęłńóśźż".indexOf(c)])
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

export const plDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("pl-PL", {
        day: "numeric",
        month: "long",
        year: "numeric",
      });
};
